import { ArrowRight, CheckCircle2, Phone, Mail, MapPin, Clock, MessageCircle, Calendar, User, Home as HomeIcon, Package, ChevronDown } from 'lucide-react';
import { useEffect } from 'react';
import Contact from '../components/Contact';

export default function Kontakt() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('kontakt');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollDown = () => {
    const element = document.getElementById('contact-methods');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const contactMethods = [
    {
      icon: Phone,
      title: 'Telefonisch',
      subtitle: 'Gratis Hotline',
      primary: '0800 700 100',
      secondary: 'Mo-Fr: 08:00 - 18:00 Uhr',
      description: 'Rufen Sie uns an für eine sofortige Beratung',
      link: 'tel:0800700100',
      color: 'green',
    },
    {
      icon: Mail,
      title: 'Per E-Mail',
      subtitle: 'Antwort innerhalb 24h',
      primary: 'info@umzugprofis.ch',
      secondary: 'Detaillierte Anfragen willkommen',
      description: 'Schreiben Sie uns Ihre Fragen',
      link: 'mailto:info@umzugprofis.ch',
      color: 'blue',
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      subtitle: 'Schnelle Antwort',
      primary: '+41 62 724 80 05',
      secondary: 'Chat mit unseren Experten',
      description: 'Direkter Kontakt per Messenger',
      link: 'https://wa.me/41627248005',
      color: 'green',
    },
  ];

  const contactReasons = [
    {
      icon: Package,
      title: 'Umzugsofferte',
      description: 'Kostenlose Besichtigung und unverbindliche Offerte für Ihren Umzug',
    },
    {
      icon: Calendar,
      title: 'Terminvereinbarung',
      description: 'Vereinbaren Sie einen Termin für Besichtigung oder Umzug',
    },
    {
      icon: MessageCircle,
      title: 'Fragen & Beratung',
      description: 'Allgemeine Fragen zu Umzügen, Zoll oder unseren Leistungen',
    },
    {
      icon: HomeIcon,
      title: 'Zusatzleistungen',
      description: 'Verpackung, Lagerung, Endreinigung oder andere Services',
    },
  ];

  const openingHours = [
    { day: 'Montag - Freitag', hours: '08:00 - 18:00 Uhr', available: true },
    { day: 'Samstag', hours: '08:00 - 13:00 Uhr', available: true },
    { day: 'Sonntag', hours: 'Geschlossen', available: false },
  ];

  const benefits = [
    'Kostenlose und unverbindliche Erstberatung',
    'Fixpreis-Offerte innerhalb von 24 Stunden',
    'Persönlicher Ansprechpartner für Ihr Projekt',
    'Flexible Terminvereinbarung auch am Wochenende',
    'Mehrsprachige Beratung (DE, EN, FR, IT)',
    'Schnelle Reaktionszeit auf alle Anfragen',
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Kontaktaufnahme',
      description: 'Sie kontaktieren uns per Telefon, E-Mail oder über das Kontaktformular.',
    },
    {
      number: '02',
      title: 'Erstberatung',
      description: 'Wir besprechen Ihre Anforderungen und vereinbaren einen Besichtigungstermin.',
    },
    {
      number: '03',
      title: 'Offerte',
      description: 'Sie erhalten eine detaillierte Fixpreis-Offerte innerhalb von 24 Stunden.',
    },
  ];

  return (
    <div className="min-h-screen">
      <section className="relative pt-24 md:pt-32 pb-24 min-h-[700px] bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-slate-500/5 animate-gradient"></div>
          <img
            src="/umzugsunternehmen-schweiz-deutschland.jpeg"
            alt="Kontakt Umzug Schweiz Deutschland"
            className="w-full h-full object-cover opacity-10"
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-green-50 text-green-700 px-4 py-2 rounded-full mb-6 border border-green-200 animate-fade-in hover:scale-105 transition-transform duration-300">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm font-semibold">Kostenlose Beratung & Offerte</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight animate-fade-in-delay">
              Kontakt
              <span className="block text-slate-700 text-4xl md:text-5xl mt-2">
                Wir sind für Sie da
              </span>
            </h1>

            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed animate-fade-in-delay-2">
              Kontaktieren Sie uns für eine kostenlose Beratung zu Ihrem Umzug von der Schweiz nach Deutschland. Unser Team steht Ihnen gerne zur Verfügung.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-12 animate-fade-in-delay-2">
              <button
                onClick={scrollToContact}
                className="group bg-slate-800 hover:bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 flex items-center space-x-2 text-lg"
              >
                <span>Direkt zum Kontaktformular</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="tel:0800700100"
                className="bg-white hover:bg-slate-50 text-slate-800 px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-xl hover:scale-105 border-2 border-slate-800"
              >
                0800 700 100 (Gratis)
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto mb-20">
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-delay-2">
                <div className="text-3xl font-bold text-slate-800 mb-1">24h</div>
                <div className="text-slate-600 text-sm">Antwortzeit garantiert</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-delay-2">
                <div className="text-3xl font-bold text-slate-800 mb-1">Gratis</div>
                <div className="text-slate-600 text-sm">Beratung & Offerte</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-delay-2">
                <div className="text-3xl font-bold text-slate-800 mb-1">100%</div>
                <div className="text-slate-600 text-sm">Unverbindlich</div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <button
            onClick={scrollDown}
            className="animate-bounce bg-white hover:bg-slate-50 text-slate-800 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-slate-200"
            aria-label="Scroll down"
          >
            <ChevronDown className="w-6 h-6" />
          </button>
        </div>
      </section>

      <section id="contact-methods" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
              So erreichen Sie uns
            </h2>
            <p className="text-xl text-slate-600 text-center">
              Wählen Sie Ihren bevorzugten Kontaktweg
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {contactMethods.map((method, index) => {
              const Icon = method.icon;
              return (
                <a
                  key={index}
                  href={method.link}
                  className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100 hover:border-slate-800 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group"
                >
                  <div className="bg-slate-800 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {method.title}
                  </h3>
                  <p className="text-sm text-green-600 font-semibold mb-4">
                    {method.subtitle}
                  </p>
                  <div className="text-xl font-bold text-slate-800 mb-2">
                    {method.primary}
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    {method.secondary}
                  </p>
                  <p className="text-slate-700">
                    {method.description}
                  </p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
              Unsere Öffnungszeiten
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl p-8 border-2 border-slate-100 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <div className="bg-slate-800 w-12 h-12 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">
                  Wir sind für Sie erreichbar
                </h3>
              </div>

              <div className="space-y-4">
                {openingHours.map((schedule, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-4 rounded-lg ${
                      schedule.available ? 'bg-green-50' : 'bg-slate-50'
                    }`}
                  >
                    <span className="font-semibold text-slate-900">
                      {schedule.day}
                    </span>
                    <span className={`font-bold ${schedule.available ? 'text-green-600' : 'text-slate-500'}`}>
                      {schedule.hours}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700">
                  <strong>Notfall oder dringende Umzüge:</strong> Für Express-Umzüge oder dringende Anfragen erreichen Sie uns auch außerhalb der regulären Öffnungszeiten per WhatsApp.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
              Wobei können wir Ihnen helfen?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {contactReasons.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100 hover:border-slate-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group"
                >
                  <div className="bg-slate-800 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {reason.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
              So läuft Ihre Kontaktaufnahme ab
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className="relative bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-slate-800 transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
              >
                <div className="absolute -top-6 -left-6 w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-xl">
                  {step.number}
                </div>
                <div className="mt-4">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
              Ihre Vorteile bei der Kontaktaufnahme
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100">
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-10 text-center text-white">
              <User className="w-16 h-16 mx-auto mb-6 text-green-400" />
              <h3 className="text-3xl font-bold mb-4">
                Bereit für Ihren Umzug?
              </h3>
              <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                Kontaktieren Sie uns jetzt für eine kostenlose und unverbindliche Beratung. Wir freuen uns darauf, Ihren Umzug von der Schweiz nach Deutschland zu realisieren.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={scrollToContact}
                  className="bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 inline-flex items-center space-x-2"
                >
                  <span>Jetzt Kontakt aufnehmen</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
                <a
                  href="tel:0800700100"
                  className="flex items-center space-x-2 text-white hover:text-slate-200 font-semibold transition"
                >
                  <Phone className="w-5 h-5" />
                  <span>0800 700 100 (Gratis)</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Unser Experten-Team
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Erfahrene Umzugsprofis, die Sie persönlich und kompetent beraten
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-20">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-slate-100 hover:border-slate-300 transition-all duration-300 hover:shadow-2xl">
                <div className="relative h-80 overflow-hidden bg-white">
                  <img
                    src="/DSC_0187.jpg"
                    alt="Herr G. Kolarov - Geschäftsführer"
                    className="w-full h-full object-contain object-[center_45%] transition-transform duration-500 hover:scale-150 scale-110"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Herr G. Kolarov</h3>
                  <p className="text-slate-600 font-medium mb-3">Geschäftsführer</p>
                  <p className="text-sm text-slate-500 italic mb-4">Über 10 Jahre Erfahrung in Umzüge ins Ausland</p>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center justify-center space-x-2">
                      <Phone className="w-4 h-4" />
                      <a href="tel:0800700100" className="hover:text-slate-900 transition">
                        0800 700 100
                      </a>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <Mail className="w-4 h-4" />
                      <a href="mailto:info@umzugprofis.ch" className="hover:text-slate-900 transition">
                        info@umzugprofis.ch
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-slate-100 hover:border-slate-300 transition-all duration-300 hover:shadow-2xl">
                <div className="relative h-80 overflow-hidden bg-white">
                  <img
                    src="/DSC_0168 copy.jpg"
                    alt="Frau D. Kolarov - Geschäftsführerin"
                    className="w-full h-full object-contain object-[center_45%] transition-transform duration-500 hover:scale-150 scale-110"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Frau D. Kolarov</h3>
                  <p className="text-slate-600 font-medium mb-3">Geschäftsführerin</p>
                  <p className="text-sm text-slate-500 italic mb-4">Über 10 Jahre Erfahrung in internationalen Umzügen und Kundenbetreuung</p>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center justify-center space-x-2">
                      <Phone className="w-4 h-4" />
                      <a href="tel:0800700100" className="hover:text-slate-900 transition">
                        0800 700 100
                      </a>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <Mail className="w-4 h-4" />
                      <a href="mailto:info@umzugprofis.ch" className="hover:text-slate-900 transition">
                        info@umzugprofis.ch
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-slate-100 hover:border-slate-300 transition-all duration-300 hover:shadow-2xl">
                <div className="relative h-80 overflow-hidden bg-white">
                  <img
                    src="/Herr-Rothenbuehler-Umzugsberater copy.jpg"
                    alt="Herr R. Rothenbühler - Umzugsberater"
                    className="w-full h-full object-contain object-center transition-transform duration-500 hover:scale-150 scale-100"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Herr R. Rothenbühler</h3>
                  <p className="text-slate-600 font-medium mb-3">Umzugsberater</p>
                  <p className="text-sm text-slate-500 italic mb-4">Über 5 Jahre Erfahrung in Auslandsumzügen, Verzollungen und Logistik</p>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center justify-center space-x-2">
                      <Phone className="w-4 h-4" />
                      <a href="tel:0800700100" className="hover:text-slate-900 transition">
                        0800 700 100
                      </a>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <Mail className="w-4 h-4" />
                      <a href="mailto:info@umzugprofis.ch" className="hover:text-slate-900 transition">
                        info@umzugprofis.ch
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                  Standort & Erreichbarkeit
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-slate-800 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">Hauptsitz</h3>
                      <p className="text-slate-600">
                        Clean & Umzug Profis AG<br />
                        Hintere Bahnhofstrasse 83<br />
                        5000 Aarau<br />
                        Schweiz
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-slate-800 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">Telefon</h3>
                      <p className="text-slate-600">
                        <a href="tel:0800700100" className="hover:text-slate-900 transition">
                          0800 700 100 (Gratis)
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-slate-800 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">E-Mail</h3>
                      <p className="text-slate-600">
                        <a href="mailto:info@umzugprofis.ch" className="hover:text-slate-900 transition">
                          info@umzugprofis.ch
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative rounded-2xl h-96 overflow-hidden shadow-xl">
                <img
                  src="/umzugsunternehmen-schweiz-deutschland.jpeg"
                  alt="Clean & Umzug Profis AG Aarau"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
